import {
  Box,
  Typography,
  Stack,
  Divider,
  Chip,
  useTheme,
  alpha,
  LinearProgress,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import RemoveIcon from "@mui/icons-material/Remove";

function fmt(n) {
  if (n == null || n === "" || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

/** Marker on a horizontal range bar: low → high, dot at `mark` (e.g. close). */
function RangeBar({ low, high, mark, label }) {
  const theme = useTheme();
  const lo = Number(low);
  const hi = Number(high);
  const m = Number(mark);
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) {
    return (
      <Box sx={{ py: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 0.5 }}>
          Range unavailable
        </Typography>
      </Box>
    );
  }
  const pct = Math.min(100, Math.max(0, ((m - lo) / (hi - lo)) * 100));
  const primary = theme.palette.primary.main;

  return (
    <Box sx={{ py: 0.75 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.75 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: "0.06em" }}>
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {fmt(lo)} → {fmt(hi)}
        </Typography>
      </Stack>
      <Box sx={{ position: "relative", height: 10, borderRadius: 5, bgcolor: alpha(theme.palette.divider, 0.35) }}>
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            borderRadius: 5,
            background: `linear-gradient(90deg, ${alpha(theme.palette.error.main, 0.25)} 0%, ${alpha(primary, 0.2)} 50%, ${alpha(theme.palette.success.main, 0.25)} 100%)`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            width: 12,
            height: 12,
            borderRadius: "50%",
            bgcolor: primary,
            border: `2px solid ${theme.palette.background.paper}`,
            boxShadow: `0 0 0 2px ${alpha(primary, 0.35)}`,
            transform: "translate(-50%, -50%)",
            left: `${pct}%`,
          }}
        />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
        Mark at {fmt(m)} ({pct.toFixed(0)}% of range)
      </Typography>
    </Box>
  );
}

export const CurrentQuote = ({ data }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  if (!data) return null;

  const open = Number(data.open);
  const close = Number(data.close);
  const high = Number(data.high);
  const low = Number(data.low);
  const vsOpen = Number.isFinite(close) && Number.isFinite(open) ? close - open : null;
  const isUp = vsOpen != null && vsOpen > 0;
  const isDown = vsOpen != null && vsOpen < 0;
  const chipColor = isUp ? theme.palette.success.main : isDown ? theme.palette.error.main : theme.palette.text.secondary;

  const demand = Number(data.totalDemand);
  const supply = Number(data.totalSupply);
  const depthTotal = (Number.isFinite(demand) ? demand : 0) + (Number.isFinite(supply) ? supply : 0);
  const bidPct = depthTotal > 0 && Number.isFinite(demand) ? Math.round((demand / depthTotal) * 100) : null;

  return (
    <Stack spacing={{ xs: 1.5, sm: 2 }} sx={{ height: "100%" }}>
      <Box
        sx={{
          borderRadius: 2,
          p: { xs: 1.25, sm: 1.75 },
          background:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.background.default, 0.5)
              : `linear-gradient(135deg, ${alpha(primary, 0.08)} 0%, ${alpha(primary, 0.02)} 55%, transparent 100%)`,
          border: `1px solid ${alpha(primary, 0.15)}`,
        }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: "0.08em" }}>
          LAST / CLOSE
        </Typography>
        <Stack direction="row" alignItems="baseline" flexWrap="wrap" gap={1} sx={{ mt: 0.5 }}>
          <Typography variant="h4" fontWeight={800} letterSpacing="-0.03em" sx={{ lineHeight: 1.1 }}>
            {fmt(data.close)}
          </Typography>
          {vsOpen != null && (
            <Chip
              size="small"
              icon={isUp ? <TrendingUpIcon /> : isDown ? <TrendingDownIcon /> : <RemoveIcon />}
              label={`${isUp ? "+" : ""}${vsOpen.toLocaleString("en-IN", { maximumFractionDigits: 2 })} vs open`}
              sx={{
                fontWeight: 700,
                bgcolor: alpha(chipColor, 0.14),
                color: chipColor,
                border: "none",
                "& .MuiChip-icon": { color: "inherit" },
              }}
            />
          )}
        </Stack>
        <Stack direction="row" spacing={2} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Open
            </Typography>
            <Typography variant="body1" fontWeight={700}>
              {fmt(data.open)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              High
            </Typography>
            <Typography variant="body1" fontWeight={700} color="success.main">
              {fmt(data.high)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Low
            </Typography>
            <Typography variant="body1" fontWeight={700} color="error.main">
              {fmt(data.low)}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <RangeBar low={data.low} high={data.high} mark={data.close} label="SESSION RANGE (LOW–HIGH)" />
      <RangeBar low={data.yearLow} high={data.yearHigh} mark={data.close} label="52-WEEK RANGE" />

      <Box>
        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: "0.06em", display: "block", mb: 1 }}>
          DEPTH (BID / ASK)
        </Typography>
        {bidPct != null ? (
          <>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="success.main" fontWeight={600}>
                Demand {fmt(data.totalDemand)}
              </Typography>
              <Typography variant="caption" color="error.main" fontWeight={600}>
                Supply {fmt(data.totalSupply)}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={bidPct}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.error.main, 0.2),
                "& .MuiLinearProgress-bar": {
                  borderRadius: 4,
                  bgcolor: theme.palette.success.main,
                },
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
              ~{bidPct}% bid-side depth
            </Typography>
          </>
        ) : (
          <Typography variant="caption" color="text.disabled">
            No depth data
          </Typography>
        )}
      </Box>

      <Divider flexItem />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 1.25,
        }}
      >
        {[
          { k: "Volume", v: data.tradeVolume },
          { k: "Buy orders", v: data.totalBuyOrders },
          { k: "Sell orders", v: data.totalSellOrders },
          { k: "Demand", v: data.totalDemand },
          { k: "Supply", v: data.totalSupply },
        ].map(({ k, v }) => (
          <Box
            key={k}
            sx={{
              p: 1,
              borderRadius: 1.5,
              border: `1px solid ${alpha(primary, 0.1)}`,
              bgcolor: alpha(primary, 0.03),
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2 }}>
              {k}
            </Typography>
            <Typography variant="body2" fontWeight={800} sx={{ mt: 0.25 }}>
              {fmt(v)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Stack>
  );
};
